import { recordUserEvent, type Client } from '@chatsdet/sdk';
import type {
  CliCommandCompletedMetadata,
  CliCommandFailedMetadata,
  CliCommandInstrumentationFailedMetadata,
  CliCommandStartedMetadata,
  UserEventOrigin,
  UserEventPayloads,
  UserEventType,
} from './types.js';

export interface UserEventRecorder {
  record<T extends UserEventType>(eventType: T, metadata: UserEventPayloads[T]): Promise<void>;
  recordCommandStarted(metadata: CliCommandStartedMetadata): Promise<void>;
  recordCommandCompleted(metadata: CliCommandCompletedMetadata): Promise<void>;
  recordCommandFailed(metadata: CliCommandFailedMetadata): Promise<void>;
  recordCommandInstrumentationFailed(
    metadata: CliCommandInstrumentationFailedMetadata
  ): Promise<void>;
}

export function createUserEventRecorder(opts: {
  client: Client;
  origin: UserEventOrigin;
  onError?: (error: unknown) => void;
}): UserEventRecorder {
  const safeRecord = async <T extends UserEventType>(
    eventType: T,
    metadata: UserEventPayloads[T]
  ): Promise<void> => {
    try {
      await recordUserEvent(opts.client, {
        eventType,
        origin: opts.origin,
        metadata,
      });
    } catch (error) {
      opts.onError?.(error);
    }
  };

  return {
    record: safeRecord,
    recordCommandStarted(metadata) {
      return safeRecord('command.started', metadata);
    },
    recordCommandCompleted(metadata) {
      return safeRecord('command.completed', metadata);
    },
    recordCommandFailed(metadata) {
      return safeRecord('command.failed', metadata);
    },
    recordCommandInstrumentationFailed(metadata) {
      return safeRecord('command.instrumentation-failed', metadata);
    },
  };
}
